// AI provider settings — pick the backend, model, and (for API providers)
// the key. Keys are write-only: the server never returns them.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MenuPicker } from '@/components/ui/menu-picker';
import { Switch } from '@/components/ui/switch';

type Provider = 'claude-cli' | 'claude-api' | 'openai' | 'deepseek';

interface AiSettingsData {
  provider: Provider;
  model: string;
  hasApiKey: boolean;
  triageEnabled: boolean;
  defaultModels: Record<Provider, string>;
}

const PROVIDER_LABELS: Record<Provider, string> = {
  'claude-cli': 'Claude subscription (this server)',
  'claude-api': 'Claude API (Anthropic key)',
  openai: 'OpenAI',
  deepseek: 'DeepSeek',
};

const KEY_HELP: Record<Provider, string> = {
  'claude-cli': 'Uses the Claude Code subscription already running on this server. No key needed.',
  'claude-api': 'Paste an Anthropic API key (sk-ant-…).',
  openai: 'Paste an OpenAI API key (sk-…).',
  deepseek: 'Paste a DeepSeek API key.',
};

export function AiSettings() {
  const qc = useQueryClient();
  const settingsQ = useQuery<AiSettingsData>({
    queryKey: ['ai-settings'],
    queryFn: async () => {
      const r = await fetch('/api/ai/settings', { credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const [provider, setProvider] = useState<Provider>('claude-cli');
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [triageEnabled, setTriageEnabled] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    if (settingsQ.data && !hydrated) {
      setProvider(settingsQ.data.provider);
      setModel(settingsQ.data.model);
      setTriageEnabled(settingsQ.data.triageEnabled ?? true);
      setHydrated(true);
    }
  }, [settingsQ.data, hydrated]);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { provider, model, triageEnabled };
      // Only send the key when the user typed one (write-only).
      if (apiKey.trim()) body.apiKey = apiKey.trim();
      const r = await fetch('/api/ai/settings', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      return r.json();
    },
    onSuccess: () => {
      setApiKey('');
      toast.success('AI settings saved');
      void qc.invalidateQueries({ queryKey: ['ai-settings'] });
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'Save failed'),
  });

  const test = useMutation({
    mutationFn: async () => {
      const r = await fetch('/api/ai/test', { method: 'POST', credentials: 'include' });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || j.error || `HTTP ${r.status}`);
      return j as { reply: string; provider: string; model: string };
    },
    onSuccess: (j) => toast.success(`Connected (${j.provider}): "${j.reply}"`),
    onError: (e) => toast.error(`Test failed: ${e instanceof Error ? e.message : 'unknown'}`),
  });

  if (settingsQ.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading AI settings…
        </CardContent>
      </Card>
    );
  }

  const data = settingsQ.data!;
  const needsKey = provider !== 'claude-cli';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          <Sparkles className="size-3.5 text-violet-500" strokeWidth={2} />
          AI provider
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Powers thread summaries (and, later, draft replies). Defaults to the Claude
          subscription on this server; switch to an API provider for billing isolation.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">Provider</span>
            <MenuPicker
              value={provider}
              onChange={(v) => {
                const p = v as Provider;
                setProvider(p);
                // Suggest the provider's default model when switching.
                setModel(data.defaultModels[p] ?? '');
              }}
              options={(Object.keys(PROVIDER_LABELS) as Provider[]).map((p) => ({
                value: p,
                label: PROVIDER_LABELS[p],
              }))}
            />
          </div>

          <label className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">Model</span>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder={
                provider === 'claude-cli'
                  ? "sonnet, opus, or a full id — blank = server default"
                  : data.defaultModels[provider]
              }
              className="block w-full rounded-md border bg-background px-2 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </label>
        </div>

        {needsKey && (
          <label className="space-y-1 text-xs">
            <span className="font-medium text-muted-foreground">
              API key{' '}
              {data.hasApiKey && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400">
                  <Check className="size-3" strokeWidth={2.5} /> a key is saved
                </span>
              )}
            </span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={data.hasApiKey ? 'Leave blank to keep the saved key' : 'Paste API key'}
              autoComplete="off"
              className="block w-full rounded-md border bg-background px-2 py-1.5 font-mono text-sm outline-none focus:ring-1 focus:ring-ring"
            />
          </label>
        )}
        <p className="text-[11px] text-muted-foreground">{KEY_HELP[provider]}</p>

        <div className="flex items-start justify-between gap-3 rounded-md border p-3">
          <div className="text-xs">
            <div className="font-medium">Inbound AI triage</div>
            <p className="mt-0.5 text-muted-foreground">
              Classifies new mail and mutes low-value notifications (promo, newsletters,
              noise). Off = every email notifies normally. Your mute/notify filter rules
              keep working either way.
            </p>
          </div>
          <Switch
            checked={triageEnabled}
            onCheckedChange={(v) => setTriageEnabled(!!v)}
            aria-label="Toggle inbound AI triage"
          />
        </div>

        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => test.mutate()}
            disabled={test.isPending || save.isPending}
          >
            {test.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Test connection
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
